import { css } from "@emotion/react";
import { Badge } from "@/components/ui/badge";
import type { PackageItem, Product } from "@/hooks/useInventoryState";
import { useShoppingCartItemsState } from "@/hooks/useShoppingCartItemsState";
import { useThemeState } from "@/hooks/useThemeState";
import { useTranslate } from "@/hooks/useTranslate/useTranslate";
import FwFunc from "@/lib/FwReact/FwFunc";
import { FwNumberDropDown } from "@/lib/FwReact/FwNumberDropDown";
import type { ImmutableObject } from "@/lib/immutable";
import type { ProductDetailsViewSettings } from "../../ProductDetails";
import { ProductDetailsPackageItemAvailability } from "../PackageItemFields/ProductDetailsPackageItemAvailability";
import { ProductDetailsPackageItemImage } from "../PackageItemFields/ProductDetailsPackageItemImage";

//------------------------------------------------------------------------------------
export type ProductDetailsPackageItemProps = {
	packageProduct: ImmutableObject<Product>;
	packageItem: ImmutableObject<PackageItem>;
	allowAddingUnavailableItems: boolean;
	enableQty: boolean;
	viewSettings: ProductDetailsViewSettings;
};
export const ProductDetailsPackageItem: React.FC<
	ProductDetailsPackageItemProps
> = (props) => {
	const packageProduct = props.packageProduct;
	const isAvailable = (packageProduct.Availability?.TotalIn ?? 0) > 0;
	const canAddToCart =
		props.enableQty && (isAvailable || props.allowAddingUnavailableItems);
	const item = props.packageItem;
	const shoppingCartItemsState = useShoppingCartItemsState();
	const themeState = useThemeState();
	const translate = useTranslate();
	const isDarkTheme = themeState.isDark();
	const isCompleteHeader = ["C", "KC"].includes(item.ItemClass ?? "");
	const isKitHeader = ["K", "CK"].includes(item.ItemClass ?? "");
	const isNestedPackageHeader = ["KC", "CK"].includes(item.ItemClass ?? "");
	const isPackageHeader = isCompleteHeader || isKitHeader;
	const isCompleteItem =
		!isPackageHeader &&
		(["CI", "CO"].includes(item.ItemClass ?? "") ||
			item.PackageItemClass === "C");
	const isKitItem =
		!isPackageHeader &&
		(["KI", "KO"].includes(item.ItemClass ?? "") ||
			item.PackageItemClass === "K");
	const packageGroup =
		isCompleteHeader || isCompleteItem
			? "complete"
			: isKitHeader || isKitItem
				? "kit"
				: "item";
	const groupColor =
		packageGroup === "complete"
			? "#7c3aed"
			: packageGroup === "kit"
				? "#16a34a"
				: "#94a3b8";
	const groupTint = isCompleteHeader
		? "#ede9fe"
		: isKitHeader
			? "#dcfce7"
			: isDarkTheme
				? "rgba(0, 0, 0, 0.18)"
				: "rgba(255, 255, 255, 0.72)";
	const iCodeTint = isCompleteItem
		? "#e9d5ff"
		: isKitItem
			? "#bbf7d0"
			: "transparent";
	const nestingLevel = isCompleteItem
		? 2
		: isNestedPackageHeader || isKitItem
			? 1
			: 0;
	const primaryTextColor =
		isPackageHeader || !isDarkTheme ? "#1f2937" : "#e5e7eb";
	const secondaryTextColor =
		isPackageHeader || !isDarkTheme ? "#475569" : "#f3f4f6";
	const showAvailability = item.Availability !== undefined;
	const showDailyRate =
		props.viewSettings.hasRates && props.viewSettings.hasDailyRate;
	const showWeeklyRate =
		props.viewSettings.hasRates && props.viewSettings.hasWeeklyRate;
	const showMonthlyRate =
		props.viewSettings.hasRates && props.viewSettings.hasMonthlyRate;

	const handleChangeQtyAsync = async (value: number): Promise<void> => {
		return Promise.resolve().then(() => {
			shoppingCartItemsState.setPackageItemQty(
				props.packageProduct,
				props.packageItem,
				value,
			);
		});
	};

	return (
		<div
			data-item-class={item.ItemClass}
			data-package-group={packageGroup}
			data-package-role={isPackageHeader ? "header" : "item"}
			data-nesting-level={nestingLevel}
			css={css`
				border: 1px solid rgba(148, 163, 184, 0.35);
				border-radius: 12px;
				padding: 0.65rem;
				background: ${groupTint};
				border-left: 4px solid ${groupColor};
				margin-left: ${nestingLevel * 0.75}rem;
				display: grid;
				grid-template-columns: 96px minmax(0, 1fr);
				gap: 0.65rem;
				align-items: start;
				@media print {
					background: transparent;
				}
			`}
		>
			<div
				css={css`
					grid-row: span 2;
					min-width: 0;
					width: 96px;
				`}
			>
				<ProductDetailsPackageItemImage
					product={packageProduct}
					packageItem={item}
				/>
			</div>
			<div
				css={css`
					min-width: 0;
					display: grid;
					gap: 0.5rem;
				`}
			>
				<div
					css={css`
						display: flex;
						align-items: center;
						flex-wrap: wrap;
						gap: 0.4rem;
					`}
				>
					<span
						css={css`
							display: inline-block;
							padding: 0.2rem 0.45rem;
							border-radius: 5px;
							background: ${iCodeTint};
							font-size: 0.7rem;
							font-weight: 700;
							line-height: 1.2;
							color: #111827;
						`}
					>
						{item.ICode}
					</span>
					{isPackageHeader && (
						<Badge
							css={css`
								border-color: ${groupColor};
								background: ${groupColor};
								color: #ffffff;
							`}
						>
							{translate(isCompleteHeader ? "Complete" : "Kit")}
						</Badge>
					)}
				</div>
				<div
					css={css`
						display: flex;
						flex-wrap: wrap;
						align-items: flex-start;
						justify-content: space-between;
						gap: 0.5rem;
					`}
				>
					<div
						css={css`
							min-width: 0;
							flex: 1 1 14rem;
							font-size: 0.82rem;
							line-height: 1.3;
							color: ${primaryTextColor};
						`}
					>
						{item.Description}
					</div>
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							justify-content: flex-end;
							gap: 0.35rem;
						`}
					>
						{item.IsOption && (
							<Badge className="border-yellow-700 bg-yellow-300 text-black">
								{translate("Optional accessories")}
							</Badge>
						)}
						<Badge className="border-orange-700 bg-orange-300 text-black">
							{`x${item.DefaultQuantity}`}
						</Badge>
					</div>
				</div>
				{props.viewSettings.hasRates && (
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							gap: 0.35rem 0.75rem;
							font-size: 0.72rem;
							color: ${secondaryTextColor};
						`}
					>
						{showDailyRate && (
							<div>{`${translate("Daily")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.DailyRate, 2, ",", ".")}`}</div>
						)}
						{showWeeklyRate && (
							<div>{`${translate("Weekly")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.WeeklyRate, 2, ",", ".")}`}</div>
						)}
						{showMonthlyRate && (
							<div>{`${translate("Monthly")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.MonthlyRate, 2, ",", ".")}`}</div>
						)}
					</div>
				)}
				{(canAddToCart || showAvailability) && (
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							align-items: center;
							gap: 0.5rem 0.75rem;
						`}
					>
						{canAddToCart &&
							(item.IsOption ? (
								<div
									css={css`
										flex: 0 0 auto;
										min-width: 5.25rem;
									`}
								>
									<FwNumberDropDown
										css={css`flex:1 1 0;`}
										label={translate("Qty")}
										value={shoppingCartItemsState.getPackageItemQty(
											props.packageProduct,
											item,
										)}
										onChange={(newValue) => {
											handleChangeQtyAsync(newValue);
										}}
									/>
								</div>
							) : (
								<div
									css={css`
										font-size: 0.76rem;
										font-weight: 600;
									color: ${secondaryTextColor};
									`}
								>
									{`${translate("Qty")}: ${item.DefaultQuantity}`}
								</div>
							))}
						{showAvailability && (
							<div
								css={css`
									display: flex;
									align-items: center;
									min-width: 0;
								`}
							>
								<ProductDetailsPackageItemAvailability packageItem={item} />
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
